import React from 'react';
import { t } from '@sm/intl';
import { generateMetricsAttribute } from '@sm/metrics';
import { createURL } from '@sm/utils';
import { Box } from '@wds/box';
import { LogoWithText } from '@sm/webassets/Logos';
import { Typography } from '@wds/typography';
import { COPY } from '../SimpleLogoHeader';
import BuildWithAISection from './BuildWithAISection';
import SplitHeroButton from './SplitHeroButton';
import { SplitHeroCTASectionProps } from '~helpers/types';

const SplitHeroCTASection = ({
  classes,
  boldHeader,
  subHeaderContent,
  splitHeroDarkBackground,
  buildWithAI,
  primaryUrl,
  primaryCtaText,
  signUpUrl,
  splitHeroSecondaryCTA,
  metricsProps,
  rebrandEP2025,
  curiosityCon,
  spotText = 'SAVE YOUR SPOT',
}: SplitHeroCTASectionProps): React.ReactElement => {
  const renderButtons = (): JSX.Element => (
    <Box className={classes.buttonBox}>
      <Box pr={6} pb={5}>
        <SplitHeroButton
          href={primaryUrl}
          text={primaryCtaText}
          metricsData={{ itemSelected: 'primary CTA', ut_ctatext: primaryCtaText }}
          {...metricsProps}
        />
      </Box>
      {splitHeroSecondaryCTA?.length !== 0 && (
        <SplitHeroButton
          href={signUpUrl}
          text={splitHeroSecondaryCTA}
          variant="ghost"
          metricsData={{ itemSelected: 'secondary CTA', ut_ctatext: splitHeroSecondaryCTA }}
          {...metricsProps}
        />
      )}
    </Box>
  );

  return (
    <Box
      display="flex"
      justifyContent="center"
      className={curiosityCon ? classes.conSection : classes.ctaSection}
      px={4}
    >
      <Box className={rebrandEP2025 ? classes.newCTAWrapper : classes.ctaWrapper}>
        <a
          aria-label={t(COPY.LOGO_ARIA_LABEL)}
          href={createURL('/', {
            ut_source: metricsProps.utSource,
            ut_source2: metricsProps.utSource2,
            ut_source3: metricsProps.utSource3,
            ut_ctatext: 'LogoWithText',
          })}
          data-sm-metrics={generateMetricsAttribute({
            data: {
              amplitudeEvent: metricsProps.amplitudeEvent,
              itemSelected: 'logo',
              templateType: metricsProps.templateType,
              surveyId: metricsProps.surveyId,
              collectorId: metricsProps.collectorId,
              respondentId: metricsProps.respondentId,
              log: {
                message: `${metricsProps.utSource}-${metricsProps.utSource2}-${metricsProps.utSource3}-LogoWithText`,
              },
            },
          })}
        >
          <LogoWithText color="alt" className={classes.logo} />
        </a>
        <Box
          className={`${classes.headerFontSize} ${rebrandEP2025 || curiosityCon ? '' : classes.balancedText}`}
          pt={5}
          pb={3}
        >
          {curiosityCon && (
            <Typography variant="bodySm" weight="medium" color="light" component="span">
              {spotText}
            </Typography>
          )}
          <Typography component="section" variant="hero1" color={splitHeroDarkBackground ? 'light' : 'dark'}>
            <span className={classes.bold}>{boldHeader}</span>
          </Typography>
        </Box>
        <Box
          pb={6}
          className={rebrandEP2025 || curiosityCon ? classes.resSubHeaderFontSize : classes.subHeaderFontSize}
        >
          <Typography
            component="p"
            variant={buildWithAI && rebrandEP2025 ? 'body' : 'cardTitle'}
            color={splitHeroDarkBackground ? 'light' : 'dark'}
          >
            {subHeaderContent}
          </Typography>
        </Box>
        {buildWithAI ? (
          <BuildWithAISection
            splitHeroPrimaryCTA={primaryCtaText}
            splitHeroSecondaryCTA={splitHeroSecondaryCTA}
            metricsProps={metricsProps}
          />
        ) : (
          renderButtons()
        )}
      </Box>
    </Box>
  );
};

export default SplitHeroCTASection;
